import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { LuCalendarDays } from "react-icons/lu";
import { MdDelete } from "react-icons/md";
import { useMutation } from "@apollo/client";
import Pagination from "../../../components/Pagination";
import { DELETE_TRANSACTION } from "../../../graphql/mutations/transaction";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../theme/color";
import fipayFonts from "../../../theme/index";
import TransactionModal from "../../../components/DashBoardComponents/Transaction/TransactionModal";

// Styled Components for Table
const Container = styled.div`
  font-family: "GT Walsheim Pro";
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const TableStyle = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f7f7f7;
  white-space: nowrap;
`;

interface TBodyTRProps {
  even: boolean;
}

const TableBodyRow = styled.tr<TBodyTRProps>`
  background-color: ${(props) => (props.even ? `${colors.White}` : "#f7f7f7")};
`;

const TableRow = styled.tr`
  height: 44px;
`;

const DateTable = styled.td`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px 24px;
  font-weight: ${fipayFonts.fipayFonts.fontWeight.regular};
  font-size: ${fipayFonts.fipayFonts.fontSize.footer1};
  letter-spacing: ${fipayFonts.fipayFonts.letterSpacing.body};
  line-height: ${fipayFonts.fipayFonts.lineHeight.footer1};
  color: ${colors.DARK_GRAY};
  border-bottom: 1px solid #ddd;
`;

const TableBody = styled.tbody`
  white-space: nowrap;
  width: full;
`;

const TableData = styled.td`
  padding: 12px 24px;
  font-size: ${fipayFonts.fipayFonts.fontSize.body3};
  color: ${colors.BLACK};
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  gap: 0.5rem;
`;

const TableAmount = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.3rem 0.75rem;
  border-radius: 12px;
  color: ${(props) =>
    props.status === "success"
      ? "green"
      : props.status === "failed"
        ? "red"
        : "orange"};
  background-color: ${(props) =>
    props.status === "success"
      ? "#e0f9e7"
      : props.status === "failed"
        ? "#f9e0e0"
        : "#f9f3e0"};
`;

const DeleteButton = styled.button`
  background-color: transparent;
  border: none;
  color: red;
  cursor: pointer;
`;

interface TDProps {
  cellWidth: string;
}
export const TD = styled.td<TDProps>`
  width: ${(props) => props.cellWidth};

  /* padding-left: 20px;
  padding-top: 5px;
  padding-bottom: 16px; */
  /* padding: 5px 15px 5px 15px ; */
  padding: 12px 20px;
  text-align: left;
  box-sizing: border-box;
`;

export const StatusTD = styled(TD)`
  /* width: 100%; */
  text-align: left;
  box-sizing: border-box;
`;

export const DeleteBox = styled.div`
  background-color: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: absolute;
  top: 70%;
  left: 90%;
  transform: translate(-50%, -50%);
  z-index: 999999;
`;

// Table Component
interface TableProps {
  headData: { label: string; cellWidth: string }[];
  bodyData?: {
    id: string;
    createdAt: string;
    productName: string;
    amount: string;
    status: string;
    qRCode?: string;
    button1?: string;
    button2?: string;
    transactionId?: string;
    type?: string;
  }[];
  status: string;
  useBodyArray?: string[];
  hideDate?: boolean;
  handleCell?: (value: any, key: string, obj: any) => any;
}

const Table: React.FC<TableProps> = ({
  headData,
  bodyData,
  status,
  useBodyArray,
  hideDate,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [transactions, setTransactions] = useState(bodyData);
  const [selectedId, setSelectedId] = useState<string>("");
  const [showDeleteBox, setShowDeleteBox] = useState<boolean>(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);

  const openModal = (id: string) => {
    setSelectedTransactionId(id);
  };
  const closeModal = () => {
    setSelectedTransactionId(null);
  };

  const recordsPerPage = 9;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;

  const displayedRecords = transactions
    ? transactions.slice(startIndex, endIndex)
    : [];

  const navigate = useNavigate();
  const deleteBoxRef = useRef<HTMLDivElement>(null);
  const [deleteTransactionMutation] = useMutation(DELETE_TRANSACTION, {
    variables: {
      deleteTransactionId: null,
    },
  });
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        deleteBoxRef.current &&
        !deleteBoxRef.current.contains(event.target as Node)
      ) {
        setShowDeleteBox(false);
      }
    };
    if (showDeleteBox) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDeleteBox]);

  useEffect(() => {
    setTransactions(bodyData);
  }, [bodyData]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    try {
      const { data } = await deleteTransactionMutation({
        variables: { deleteTransactionId: selectedId },
      });

      setTransactions((prevTransactions) => {
        if (!prevTransactions) return prevTransactions;
        return prevTransactions.filter(
          (transaction) => transaction.id !== selectedId
        );
      });
      setShowDeleteBox(false);
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  return (
    <Container>
      <TableContainer>
        <TableStyle>
          <TableHead>
            <TableRow>
              {headData.map((header, index) => (
                <TableHeader key={index}>{header.label}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRecords.map((body: any, index) => {
              const tableBody = { ...body };
              tableBody.status =
                tableBody.status || tableBody.accountStatus || "";
              return (
                <TableBodyRow key={index} even={index % 2 === 0}>
                  {!hideDate && (
                    <TableData>
                      <DateTable>
                        <LuCalendarDays size={"20px"} color={"#7A8699"} />
                        {new Date(
                          parseInt(tableBody?.createdAt)
                        ).toLocaleString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })}
                        <span
                          style={{
                            fontFamily: "GT Walsheim Pro",
                            fontSize: "14px",
                            fontWeight: 400,
                            lineHeight: "20px",
                            letterSpacing: "0.02em",
                            color: "#868686",
                            marginLeft: "5px",
                          }}
                        >
                          {new Date(
                            parseInt(tableBody?.createdAt)
                          ).toLocaleString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                      </DateTable>
                    </TableData>
                  )}
                  {useBodyArray ? (
                    useBodyArray?.map((key) => (
                      <TableData>
                        {["amount", "price", "currency"].includes(key) ? (
                          <TableAmount>
                            {props?.handleCell?.(
                              tableBody?.[key] as any,
                              key,
                              tableBody
                            )}
                          </TableAmount>
                        ) : (
                          props?.handleCell?.(
                            tableBody?.[key] as any,
                            key,
                            tableBody
                          )
                        )}
                      </TableData>
                    ))
                  ) : (
                    <>
                      <TableData>{tableBody.productName}</TableData>
                      <TableData>
                        <TableAmount>{tableBody.amount}</TableAmount>
                      </TableData>
                      <TableData>{tableBody.type}</TableData>
                      <TableData>
                        {" "}
                        {tableBody?.id?.substring(0, 18) + ""}
                      </TableData>
                    </>
                  )}
                  <TableData>
                    <StatusBadge status={tableBody.status}>
                      {tableBody?.status?.charAt(0)?.toUpperCase() +
                        tableBody?.status?.slice(1)}
                    </StatusBadge>
                  </TableData>
                  <TableData>
                    <img
                      src="/assets/svg/icon.svg"
                      alt=""
                      style={{ cursor: "pointer" }}
                      key={index}
                      onClick={() => openModal(tableBody.id)}
                    />

                    {showDeleteBox && selectedId === tableBody.id && (
                      <DeleteBox ref={deleteBoxRef}>
                        <DeleteButton onClick={handleDelete}>
                          Delete <MdDelete />
                        </DeleteButton>
                      </DeleteBox>
                    )}
                  </TableData>
                </TableBodyRow>
              );
            })}
          </TableBody>
        </TableStyle>
      </TableContainer>
      {selectedTransactionId && (
        <TransactionModal
          transactionId={selectedTransactionId}
          onClose={closeModal}
        />
      )}

      <Pagination
        totalPages={Math.ceil((bodyData?.length ?? 0) / recordsPerPage)}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default Table;
